import { useQuery } from '@tanstack/react-query';

import { adminFetch } from '@/hooks/admin/useAdminApi';
import { supabase } from '@/lib/supabase';

/**
 * Credits — money we have taken that is not yet sitting on an invoice.
 *
 * Two shapes of the same fact, because the portal has two shapes of login:
 *   useAccountCredits  the account you are standing on (orders.v_account_credits,
 *                      RLS-scoped by current_customer_id()).
 *   useGroupCredits    every store in the chain, for a chain accounting login
 *                      (customer-group-credits, service-role + roster-scoped).
 *
 * A credit reduces what is owed, so it belongs beside the invoices. Live proof:
 * THE MELT MAIN's open invoices came to $9,918.00 while QuickBooks put the
 * account balance at $2,792.70 — the $7,125.30 difference being 15 unapplied
 * payments the portal could not render.
 */

export type CreditKind = 'payment' | 'credit_memo';

export interface CreditRow {
  kind: CreditKind;
  /** QBO Payment id, or CreditMemo id — unique within its kind. */
  sourceId: string;
  txnDate: string | null;
  /** Cheque number, ACH reference, or the credit memo's document number. */
  reference: string | null;
  method: string | null;
  /** What we took in. */
  receivedAmount: number;
  /** How much of it has already landed on invoices. */
  appliedAmount: number;
  /** What is left over — the part that is still a credit on the account. */
  creditAmount: number;
  /** How many invoices it was applied to (payments only). */
  appliedToCount: number;
  ageDays: number;
  /** Chain rows only — which store's books it sits on. */
  storeName?: string | null;
  storeCode?: string | null;
  customerId?: string | null;
}

interface CreditDbRow {
  kind: CreditKind;
  source_id: string;
  txn_date: string | null;
  reference: string | null;
  method: string | null;
  received_amount: number | string | null;
  applied_amount: number | string | null;
  credit_amount: number | string | null;
  applied_to_count: number | null;
  age_days: number | null;
  customer_id?: string | null;
  customer_name?: string | null;
}

const n = (v: number | string | null | undefined): number => {
  const x = typeof v === 'number' ? v : Number(v ?? 0);
  return Number.isFinite(x) ? x : 0;
};

function mapRow(r: CreditDbRow): CreditRow {
  return {
    kind: r.kind,
    sourceId: String(r.source_id),
    txnDate: r.txn_date,
    reference: r.reference,
    method: r.method,
    receivedAmount: n(r.received_amount),
    appliedAmount: n(r.applied_amount),
    creditAmount: n(r.credit_amount),
    appliedToCount: r.applied_to_count ?? 0,
    ageDays: r.age_days ?? 0,
    storeName: r.customer_name ?? null,
    customerId: r.customer_id ?? null,
  };
}

const SELECT_COLS =
  'kind, source_id, customer_id, customer_name, txn_date, reference, method, ' +
  'received_amount, applied_amount, credit_amount, applied_to_count, age_days';

/** Credits on the account you are standing on. */
export function useAccountCredits() {
  return useQuery<CreditRow[]>({
    queryKey: ['account-credits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_account_credits')
        .select(SELECT_COLS)
        .order('txn_date', { ascending: true });
      // ⚠ Never swallow this into an empty list. "You have no credits" and "we
      // could not read your credits" look identical on screen and only one of
      // them is true — the same swallow that made a 400 read as an empty order
      // history in 1.141.
      if (error) throw new Error(error.message);
      return (data ?? []).map((r) => mapRow(r as unknown as CreditDbRow));
    },
    staleTime: 60 * 1000,
  });
}

export interface CreditApplication {
  qboInvoiceId: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  amount: number;
}

interface ApplicationDbRow {
  qbo_payment_id: string;
  qbo_invoice_id: string;
  invoice_number: string | null;
  invoice_date: string | null;
  amount: number | string | null;
}

/**
 * What each payment was applied to, keyed by payment id. One read for the whole
 * page rather than one per expanded row — a Melt payment routinely covers 39
 * invoices and the page opens with several rows already expandable.
 */
export function usePaymentApplications(paymentIds: string[]) {
  const key = [...paymentIds].sort().join(',');
  return useQuery<Map<string, CreditApplication[]>>({
    queryKey: ['payment-applications', key],
    enabled: paymentIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_payment_applications')
        .select('qbo_payment_id, qbo_invoice_id, invoice_number, invoice_date, amount')
        .in('qbo_payment_id', paymentIds);
      if (error) throw new Error(error.message);
      const out = new Map<string, CreditApplication[]>();
      for (const raw of data ?? []) {
        const r = raw as unknown as ApplicationDbRow;
        const list = out.get(r.qbo_payment_id) ?? [];
        list.push({
          qboInvoiceId: String(r.qbo_invoice_id),
          invoiceNumber: r.invoice_number,
          invoiceDate: r.invoice_date,
          amount: n(r.amount),
        });
        out.set(r.qbo_payment_id, list);
      }
      return out;
    },
    staleTime: 60 * 1000,
  });
}

export interface GroupCreditRow extends CreditRow {
  appliedTo: CreditApplication[];
}

export interface GroupCreditsResponse {
  ok: true;
  is_group: boolean;
  master?: { customer_id: string; name: string; group_brand: string | null };
  stores: Array<{ customer_id: string; name: string; store_code: string | null; is_master: boolean }>;
  credits: Array<{
    kind: CreditKind;
    source_id: string;
    txn_date: string | null;
    reference: string | null;
    method: string | null;
    received_amount: number;
    applied_amount: number;
    credit_amount: number;
    age_days: number;
    customer_id: string;
    store_name: string;
    store_code: string | null;
    is_master: boolean;
    applied_to: Array<{
      qbo_invoice_id: string;
      invoice_number: string | null;
      invoice_date: string | null;
      amount: number;
    }>;
  }>;
  totals?: {
    credit_count: number;
    credit_total: number;
    payment_total: number;
    credit_memo_total: number;
  };
}

/**
 * Every store's credits, for a chain accounting login. `is_group: false` is the
 * normal answer for everyone else, not an error — the page falls back to the
 * single-account view on it.
 */
export function useGroupCredits() {
  return useQuery<GroupCreditsResponse>({
    queryKey: ['group-credits'],
    queryFn: () => adminFetch<GroupCreditsResponse>('customer-group-credits'),
    staleTime: 60 * 1000,
    retry: 1,
  });
}
